import React, { memo, FunctionComponent, SVGAttributes } from 'react';
import classnames from 'classnames';
import { motion, Transition } from 'framer-motion';
import { noTransition } from './utils';

const R = Math.PI / 180;

const getDominantBaseline = (isUp: boolean, isRight: boolean, isDown: boolean, isLeft: boolean) => {
  if (isLeft || isRight) return 'middle';
  if (isUp) return 'text-after-edge';
  if (isDown) return 'text-before-edge';
};

const getTextAnchor = (isUp: boolean, isRight: boolean, isDown: boolean, isLeft: boolean) => {
  if (isUp || isDown) return 'middle';
  if (isRight) return 'start';
  if (isLeft) return 'end';
};

export interface Props extends SVGAttributes<SVGGElement> {
  animate?: boolean;
  fill?: string;
  labelOffset?: number;
  stroke?: string;
  transition?: Transition;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export const VizSVGAxisTick: FunctionComponent<Props> = (props: Props) => {
  const {
    animate,
    children,
    className,
    fill,
    labelOffset = 0,
    stroke,
    transition,
    x0,
    x1,
    y0,
    y1,
    ...rest
  } = props;

  const r = Math.atan2(y1 - y0, x1 - x0);

  const isUp = r > R * 247.5 || r < R * -67.5;
  const isRight = r >= R * -67.5 && r <= R * 67.5;
  const isDown = r > R * 67.5 && r < R * 112.5;
  const isLeft = r >= R * 112.5 && r <= R * 247.5;

  const dominantBaseline = getDominantBaseline(isUp, isRight, isDown, isLeft);
  const textAnchor = getTextAnchor(isUp, isRight, isDown, isLeft);
  const labelOffsetX = labelOffset && Math.cos(r) * labelOffset;
  const labelOffsetY = labelOffset && Math.sin(r) * labelOffset;

  return (
    <g { ...rest }
        className={ classnames('Bitviz__axis-tick', className) }>
      <motion.g
          animate={ { opacity: 1, x: x0, y: y0 } }
          initial={ { opacity: 0, x: x0, y: y0 } }
          transition={ animate ? transition : noTransition }>
        { children !== undefined && (
          <line
              className="Bitviz__axis-tick-line"
              stroke={ stroke }
              x1="0"
              x2={ x1 - x0 }
              y1="0"
              y2={ y1 - y0 } />
        ) }

        <text
            className="Bitviz__axis-tick-label"
            dominantBaseline={ dominantBaseline }
            fill={ fill }
            textAnchor={ textAnchor }
            transform={ `translate(${(x1 - x0) + labelOffsetX}, ${(y1 - y0) + labelOffsetY})` }>
          { children }
        </text>
      </motion.g>
    </g>
  );
};

export default memo(VizSVGAxisTick);
